<script lang="ts" setup>
import { Tooltip } from "floating-vue";
import { onBeforeMount, onMounted, ref, watch } from "vue";
import { MerchantType } from "@/enums";
import { useRenderStore } from "@/builder/stores/renderStore";
import {
    TestPaymentMethodsStripe,
    TestPaymentMethodsAuthNet,
} from "@/builder/enums";
const renderStore = useRenderStore();

// refs
const disabledTooltip = ref(false);

const testCard = ref("");
const isTestCardCopied = ref(false);
const testCardTooltip = ref("Copy to clipboard");

const testExpiration = ref("");
const isTestExpCopied = ref(false);
const testExpTooltip = ref("Copy to clipboard");

const testCvc = ref("123");
const isTestCvcCopied = ref(false);
const testCvcTooltip = ref("Copy to clipboard");

const testPaymentMethods =
    renderStore.page.merchantType === MerchantType.StripeUnified
        ? TestPaymentMethodsStripe
        : TestPaymentMethodsAuthNet;

// lifecycle hooks
onBeforeMount(() => {
    isSmallerScreen();
});

onMounted(async () => {
    if (renderStore.page.merchantType === MerchantType.StripeUnified) {
        testCard.value = TestPaymentMethodsStripe.Successful_Visa;
    } else {
        testCard.value = TestPaymentMethodsAuthNet.Successful_Visa;
    }
    setTestExpiration();
    setTestCvc();
});

// methods
const isSmallerScreen = () => {
    disabledTooltip.value = window.innerWidth < 768;
};

const cleanTestPayments = (payments) => {
    const testCardSplit = payments.split(/_(.*)/s);

    testCardSplit[0] += " -";

    if (testCardSplit[1].match(/.*[A-Z].*[A-Z].*/g)) {
        testCardSplit[1] = testCardSplit[1].split(/(?=[A-Z])/).join(" ");
    }

    return testCardSplit.join(" ");
};

const copyTestCard = () => {
    navigator.clipboard.writeText(testCard.value);
    isTestCardCopied.value = true;
    testCardTooltip.value = "Copied!";

    setTimeout(function () {
        isTestCardCopied.value = false;
        testCardTooltip.value = "Copy to clipboard";
    }, 2000);
};

const setTestExpiration = () => {
    const date = new Date();
    const year = date.getFullYear() + 2;
    testExpiration.value = "12/" + year.toString().substring(2);
};

const setTestCvc = () => {
    testCard.value.startsWith("3782")
        ? (testCvc.value = "1234")
        : (testCvc.value = "123");
};

const copyTestExpiration = () => {
    navigator.clipboard.writeText(testExpiration.value);
    isTestExpCopied.value = true;
    testExpTooltip.value = "Copied!";

    setTimeout(function () {
        isTestExpCopied.value = false;
        testExpTooltip.value = "Copy to clipboard";
    }, 2000);
};

const copyTestCvc = () => {
    navigator.clipboard.writeText(testCvc.value);
    isTestCvcCopied.value = true;
    testCvcTooltip.value = "Copied!";

    setTimeout(function () {
        isTestCvcCopied.value = false;
        testCvcTooltip.value = "Copy to clipboard";
    }, 2000);
};

// watchers
watch(testCard, (newVal) => {
    if (newVal) {
        setTestCvc();
    }
});

// Mobile resizing event
window.addEventListener("resize", isSmallerScreen);
</script>

<template>
    <div class="rd-flex rd-gap-x-5 rd-w-full rd-flex-col md:rd-flex-row">
        <div class="rd-w-full rd-mb-2 md:rd-w-1/3 md:rd-mb-0">
            <label for="testCards" class="rd-sr-only">
                Test Payment Methods
            </label>
            <select id="testCards" v-model="testCard" class="rd-pr-[30px]">
                <option disabled value="">Please select</option>
                <option v-for="(item, index) in testPaymentMethods" :key="item" :value="item">
                    {{ cleanTestPayments(index) }}
                </option>
            </select>
        </div>

        <div class="rd-grid rd-grid-cols-12 rd-gap-x-5 md:rd-flex md:rd-w-2/3">
            <div class="rd-relative rd-col-span-12 rd-mb-2 md:rd-mb-0 md:rd-w-1/2">
                <label for="txtTestCard" class="rd-sr-only">
                    Test Card Number
                </label>

                <input id="txtTestCard" readonly type="text" v-model="testCard" :disabled="!testCard" />

                <Tooltip distance="-4" container=".test-payment-types" delay="15" popperClass="tooltip-sm"
                    :disabled="disabledTooltip" class="rd-absolute rd-right-0 rd-top-0">
                    <button type="button" class="rd-py-[13px] rd-px-[15px] copy-card" @click="copyTestCard"
                        :disabled="!testCard">
                        <i v-if="!isTestCardCopied" class="fa-solid fa-copy rd-text-base rd-leading-none"
                            aria-hidden="true"></i>
                        <i v-else class="fa-solid fa-check rd-text-base rd-leading-none" aria-hidden="true"></i>
                        <span class="rd-sr-only">Copy Test Card Number to clipboard</span>
                    </button>
                    <template #popper>
                        {{ testCardTooltip }}
                    </template>
                </Tooltip>
            </div>

            <div class="rd-relative rd-col-span-6 md:rd-w-1/4">
                <label for="txtTestExpiration" class="rd-sr-only">Test Expiration Date</label>

                <input id="txtTestExpiration" readonly type="text" v-model="testExpiration" :disabled="!testCard" />

                <Tooltip distance="-4" container=".test-payment-types" delay="15" popperClass="tooltip-sm"
                    :disabled="disabledTooltip" class="rd-absolute rd-right-0 rd-top-0">
                    <button type="button" class="rd-py-[13px] rd-px-[15px] copy-card" @click="copyTestExpiration"
                        title="Copy to clipboard" :disabled="!testCard">
                        <i v-if="!isTestExpCopied" class="fa-solid fa-copy rd-text-base rd-leading-none"
                            aria-hidden="true"></i>
                        <i v-else class="fa-solid fa-check rd-text-base rd-leading-none" aria-hidden="true"></i>
                        <span class="rd-sr-only">Copy Test Expiration Date to clipboard</span>
                    </button>
                    <template #popper>
                        {{ testExpTooltip }}
                    </template>
                </Tooltip>
            </div>

            <div class="rd-relative rd-col-span-6 md:rd-w-1/4">
                <label for="txtTestCvc" class="rd-sr-only">Test Security Code</label>

                <input id="txtTestCvc" readonly type="text" v-model="testCvc" :disabled="!testCard" />

                <Tooltip distance="-4" container=".test-payment-types" delay="15" popperClass="tooltip-sm"
                    :disabled="disabledTooltip" class="rd-absolute rd-right-0 rd-top-0">
                    <button type="button" class="rd-py-[13px] rd-px-[15px] copy-card" @click="copyTestCvc"
                        title="Copy to clipboard" :disabled="!testCard">
                        <i v-if="!isTestCvcCopied" class="fa-solid fa-copy rd-text-base rd-leading-none"
                            aria-hidden="true"></i>
                        <i v-else class="fa-solid fa-check rd-text-base rd-leading-none" aria-hidden="true"></i>
                        <span class="rd-sr-only">Copy Test Security Code to clipboard</span>
                    </button>
                    <template #popper>
                        {{ testCvcTooltip }}
                    </template>
                </Tooltip>
            </div>
        </div>
    </div>
</template>
